/**
 * TeamsSection - Settings panel for admin team management.
 *
 * Teams decide which users can see and share threads with each other (see
 * `Team` in @taskin-code/contracts). Follows the same pattern as UsersSection:
 * manual `refresh()` + useState, gated on `u.view` to view and `u.edit` to
 * mutate, `window.confirm` for delete.
 *
 * The membership dialog's candidate list comes from `GET /api/admin/users`
 * (the full admin roster), not `/api/users/directory` — the directory is
 * itself filtered by team membership, so it cannot be used to grant the very
 * membership that would unlock it.
 */
import type { TeamId, TeamWithMembers, UserId } from "@taskin-code/contracts";
import { TEAM_NAME_MAX_LENGTH } from "@taskin-code/contracts";
import { useEffect, useMemo, useState } from "react";

import { useAuthStore } from "../../auth/authStore";
import { TeamsClient } from "../../auth/TeamsClient";
import { adminFetch } from "../../lib/adminFetch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { UserAvatar } from "../UserAvatar";
import { Field, ModalShell } from "./settingsModal";
import { resolveMemberNames, sortTeamsByName, validateTeamName } from "./TeamsSection.logic";

interface AdminUserRow {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly avatarFileName: string | null;
}

export function TeamsSection() {
  const isAdmin = useAuthStore((state) => state.globalPermissions.has("u.view"));
  const canEdit = useAuthStore((state) => state.globalPermissions.has("u.edit"));
  const [teams, setTeams] = useState<ReadonlyArray<TeamWithMembers>>([]);
  const [users, setUsers] = useState<ReadonlyArray<AdminUserRow>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [renamingTeamId, setRenamingTeamId] = useState<TeamId | null>(null);
  const [managingTeamId, setManagingTeamId] = useState<TeamId | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [teamsResult, usersResult] = await Promise.all([
        TeamsClient.list(),
        adminFetch<{ users: ReadonlyArray<AdminUserRow> }>("/api/admin/users"),
      ]);
      setTeams(teamsResult);
      setUsers(usersResult.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    void refresh();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        You need the <code>u.view</code> permission to manage teams.
      </div>
    );
  }

  const userNames: ReadonlyArray<{ userId: UserId; name: string }> = users.map((u) => ({
    userId: u.userId as UserId,
    name: u.name,
  }));
  const sortedTeams = sortTeamsByName(teams);
  const renamingTeam = teams.find((t) => t.id === renamingTeamId) ?? null;
  const managingTeam = teams.find((t) => t.id === managingTeamId) ?? null;

  const removeTeam = async (team: TeamWithMembers) => {
    if (!window.confirm(`Delete team "${team.name}"? Members lose the visibility it grants.`)) {
      return;
    }
    setError(null);
    try {
      await TeamsClient.remove(team.id);
      void refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete team");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">Teams</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Group users so they can see and share threads with each other.
          </p>
        </div>
        <Button
          size="sm"
          className="shrink-0"
          disabled={!canEdit}
          onClick={() => setCreateOpen(true)}
        >
          + New team
        </Button>
      </div>

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border/60 bg-card/40">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-border/40 bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-[24%] px-4 py-2 font-medium">Team</th>
              <th className="w-[10%] px-4 py-2 font-medium">Members</th>
              <th className="px-4 py-2 font-medium">Who's in it</th>
              <th className="w-[1%] px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && sortedTeams.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading teams…
                </td>
              </tr>
            ) : sortedTeams.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No teams yet.
                </td>
              </tr>
            ) : (
              sortedTeams.map((team) => {
                const names = resolveMemberNames(team.memberIds, userNames);
                return (
                  <tr key={team.id} className="border-b border-border/30 align-top last:border-b-0">
                    <td className="px-4 py-3 font-medium">{team.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{team.memberIds.length}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {names.length === 0 ? "—" : names.join(", ")}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setManagingTeamId(team.id)}
                      >
                        Members
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2"
                        disabled={!canEdit}
                        onClick={() => setRenamingTeamId(team.id)}
                      >
                        Rename
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 text-red-500 hover:text-red-400"
                        disabled={!canEdit}
                        onClick={() => void removeTeam(team)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <TeamNameDialog
          title="Create team"
          submitLabel="Create"
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name) => {
            await TeamsClient.create(name);
          }}
          onDone={() => {
            setCreateOpen(false);
            void refresh();
          }}
        />
      ) : null}

      {renamingTeam ? (
        <TeamNameDialog
          title={`Rename "${renamingTeam.name}"`}
          submitLabel="Save"
          initialName={renamingTeam.name}
          onClose={() => setRenamingTeamId(null)}
          onSubmit={async (name) => {
            await TeamsClient.rename(renamingTeam.id, name);
          }}
          onDone={() => {
            setRenamingTeamId(null);
            void refresh();
          }}
        />
      ) : null}

      {managingTeam ? (
        <ManageMembersDialog
          team={managingTeam}
          users={users}
          canEdit={canEdit}
          onClose={() => setManagingTeamId(null)}
          onChanged={() => void refresh()}
        />
      ) : null}
    </div>
  );
}

function TeamNameDialog(props: {
  title: string;
  submitLabel: string;
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
  onDone: () => void;
}) {
  const [name, setName] = useState(props.initialName ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const validation = validateTeamName(name);
    if (!validation.ok) {
      setError(validation.error ?? "Invalid name");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await props.onSubmit(name.trim());
      props.onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save team");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={props.title} onClose={props.onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={TEAM_NAME_MAX_LENGTH}
            required
            autoFocus
          />
        </Field>
        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Saving…" : props.submitLabel}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ManageMembersDialog(props: {
  team: TeamWithMembers;
  users: ReadonlyArray<AdminUserRow>;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { team } = props;
  const [error, setError] = useState<string | null>(null);
  const memberIds = useMemo(() => new Set<string>(team.memberIds), [team.memberIds]);

  const toggleMember = async (userId: string, isMember: boolean) => {
    setError(null);
    try {
      if (isMember) {
        await TeamsClient.removeMember(team.id, userId as UserId);
      } else {
        await TeamsClient.addMember(team.id, userId as UserId);
      }
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update membership");
    }
  };

  const sortedUsers = useMemo(
    () => props.users.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [props.users],
  );

  return (
    <ModalShell title={`Members of "${team.name}"`} onClose={props.onClose} wide>
      <div className="flex flex-col gap-3">
        {sortedUsers.length === 0 ? (
          <p className="text-xs text-muted-foreground">No users to add yet.</p>
        ) : (
          <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {sortedUsers.map((user) => {
              const isMember = memberIds.has(user.userId);
              return (
                <label
                  key={user.userId}
                  className="flex items-center justify-between gap-3 rounded-md border border-border/40 bg-card/30 px-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <UserAvatar
                      userId={user.userId}
                      displayName={user.name}
                      avatarFileName={user.avatarFileName}
                      size="sm"
                      className="shrink-0"
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">{user.name}</span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={isMember}
                    disabled={!props.canEdit}
                    onChange={() => void toggleMember(user.userId, isMember)}
                    className="size-4"
                  />
                </label>
              );
            })}
          </div>
        )}

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={props.onClose}>
            Done
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
